"use client";

import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Light-themed newsletter capture for the Floral Journal (WS2). Same endpoint
 * as the footer signup (/api/newsletter/subscribe) but styled with the normal
 * design tokens instead of the dark header-* footer tokens, so it sits cleanly
 * inside a storefront section band.
 */
export function JournalNewsletter() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<"new" | "already" | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "blog" }),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string; outcome?: string };
      if (!res.ok) {
        toast.error(json.error ?? "Couldn't subscribe right now.");
        return;
      }
      setDone(json.outcome === "already" ? "already" : "new");
      toast.success("You're on the list!");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-border/60 bg-background p-7 text-center shadow-sm md:p-10">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <Mail className="h-6 w-6 text-primary" aria-hidden="true" />
      </div>
      <h2 className="mt-4 heading-section">Seasonal notes from the studio</h2>
      <p className="mx-auto mt-2 max-w-md text-muted-foreground">
        New guides, seasonal flowers and gift ideas — a gentle email now and then, never spam.
      </p>

      {done !== null ? (
        <div className="mt-6 flex items-center justify-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3" role="status" aria-live="polite">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <p className="text-sm text-foreground/80">
            {done === "already" ? "You're already on our list — thank you!" : "You're on the list — thank you!"}
          </p>
        </div>
      ) : (
        <>
          <form onSubmit={submit} className="mx-auto mt-6 flex max-w-md flex-col gap-2.5 sm:flex-row" aria-label="Newsletter signup">
            <label htmlFor="journal-newsletter-email" className="sr-only">Email address</label>
            <Input
              id="journal-newsletter-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              maxLength={254}
              className="h-12 flex-1 rounded-lg"
            />
            <Button type="submit" size="lg" disabled={submitting} className="h-12 shrink-0">
              {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Joining…</>) : "Subscribe"}
            </Button>
          </form>
          <p className="mt-2.5 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            No spam — unsubscribe anytime.
          </p>
        </>
      )}
    </div>
  );
}
